/**
 * Scene — Three.js canvas + 3D Tiles renderer + controls.
 *
 * Mirrors the desktop viewer's `scene.js` + `tiles-loader.js` flow.
 * Camera/renderer/controls construction is portable Three.js; the
 * tileset transform + camera framing live inside loadTiles() (see
 * TilesetLoader.ts) so we don't have to re-invent the ECEF→ENU pipeline
 * here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  PerspectiveCamera,
  Raycaster,
  Scene as ThreeScene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three'

import { CoordinateManager } from './Coordinates'
import { Controls } from './Controls'
import { Picking } from './Picking'
import {
  TilesetMode,
  detectMode,
  loadTiles,
  updateTiles,
} from './TilesetLoader'
import { ActiveTool, Toolbar } from '../ui/Toolbar'
import { StatusBar } from '../ui/StatusBar'
import { MeasurementsPanel } from '../ui/MeasurementsPanel'
import { logout } from '../auth/webAuthClient'
import { uploadThumbnail } from '../api/projectClient'
import {
  createAnnotation,
  deleteAnnotation,
  ServerAnnotation,
} from '../api/annotationsClient'
import {
  deleteScreenshot,
  getScreenshotDownloadUrl,
  ServerScreenshot,
  uploadScreenshot,
} from '../api/screenshotsClient'
import { ScreenshotsPanel } from '../ui/ScreenshotsPanel'
import { makeZipBlob, ZipEntry } from '../utils/zip'
import { DistanceTool } from './tools/DistanceTool'
import { AreaTool } from './tools/AreaTool'
import { NoteTool } from './tools/NoteTool'
import type { Measurement } from './measurements'
import {
  ensureAuthorChip,
  measurementFromServer,
  restoreAnnotations,
  setAuthorChipsVisible,
  toCreatePayload,
} from './annotations'
import { useRealtime } from '../realtime/RealtimeProvider'
import { CursorsManager } from '../realtime/cursors'
import { PresencePanel } from '../ui/PresencePanel'
import { MeasurementTooltip } from '../ui/MeasurementTooltip'
import type { TilesRenderer } from '3d-tiles-renderer'

/** How many pixels the cursor may travel between mousedown and mouseup
 * to still count as a click (instead of a drag).  ~5 px works well on
 * both mice and trackpads. */
const CLICK_DRAG_THRESHOLD_PX = 5

interface SceneProps {
  /** When set, measurements are persisted to the backend and visible to
   * teammates. Omit for `?dev=<url>` mode (ephemeral viewing). */
  projectId?: string
  tilesetUrl: string
  /** Existing annotations from the backend — restored as Three.js geometry on first paint. */
  initialAnnotations?: ServerAnnotation[]
  /** Existing screenshots from the backend — populates the gallery. */
  initialScreenshots?: ServerScreenshot[]
  /**
   * Project UUID for which to capture & upload a thumbnail once the
   * scene has stabilised. Undefined = skip (project already has one
   * that loads correctly).
   */
  captureThumbnailFor?: string
}

export function Scene({
  projectId,
  tilesetUrl,
  initialAnnotations,
  initialScreenshots,
  captureThumbnailFor,
}: SceneProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeTool, setActiveTool] = useState<ActiveTool>('none')
  const [statusMessage, setStatusMessage] = useState<string>('Lade Tileset…')
  const [showHint, setShowHint] = useState<boolean>(true)
  const [color, setColor] = useState<number>(0xffd400)
  const [lineScale, setLineScale] = useState<number>(1)
  const [labelScale, setLabelScale] = useState<number>(1)
  const [measureScale, setMeasureScale] = useState<number>(1)
  const [measurements, setMeasurements] = useState<Measurement[]>([])
  const [showAuthors, setShowAuthors] = useState<boolean>(false)
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null)
  const [screenshots, setScreenshots] = useState<ServerScreenshot[]>(initialScreenshots ?? [])
  const [screenshotBusy, setScreenshotBusy] = useState<boolean>(false)
  const [screenshotError, setScreenshotError] = useState<string | null>(null)
  const [zipBusy, setZipBusy] = useState<boolean>(false)

  const realtime = useRealtime()
  const realtimeRef = useRef(realtime)
  useEffect(() => { realtimeRef.current = realtime }, [realtime])

  // Keep a ref of the latest measurements list so the (stable-closure)
  // mousemove handler inside the main useEffect can raycast against the
  // current scene without re-mounting on every state update.
  const measurementsRef = useRef<Measurement[]>([])
  useEffect(() => { measurementsRef.current = measurements }, [measurements])
  const showAuthorsRef = useRef(showAuthors)
  useEffect(() => { showAuthorsRef.current = showAuthors }, [showAuthors])

  // Re-apply chip visibility whenever the toggle flips or the
  // measurement list changes (newly created / restored items).
  useEffect(() => {
    setAuthorChipsVisible(measurements, showAuthors)
  }, [measurements, showAuthors])

  const activeToolRef = useRef<ActiveTool>(activeTool)
  useEffect(() => {
    activeToolRef.current = activeTool
  }, [activeTool])

  const distanceToolRef = useRef<DistanceTool | null>(null)
  const areaToolRef = useRef<AreaTool | null>(null)
  const noteToolRef = useRef<NoteTool | null>(null)
  const cursorsManagerRef = useRef<CursorsManager | null>(null)
  const printFnRef = useRef<(() => void) | null>(null)
  const captureScreenshotRef = useRef<(() => Promise<Blob | null>) | null>(null)

  // Keep the cursor manager's "who is in the room" view in sync with
  // presence updates from the realtime provider. The ref may be null
  // before the tileset finishes loading; the main useEffect re-applies
  // the latest members once the manager exists.
  useEffect(() => {
    cursorsManagerRef.current?.setMembers(realtime.members)
  }, [realtime.members])

  const handlePrint = useCallback(() => {
    printFnRef.current?.()
  }, [])

  const handleScreenshot = useCallback(async () => {
    const capture = captureScreenshotRef.current
    if (!capture || screenshotBusy) return
    setScreenshotBusy(true)
    setScreenshotError(null)
    try {
      const blob = await capture()
      if (!blob) {
        setScreenshotError('Screenshot konnte nicht erstellt werden.')
        return
      }

      if (projectId) {
        // Real project: persist to the backend; the gallery row's
        // thumbnail (and ⬇ button) is what the user clicks to download.
        try {
          const saved = await uploadScreenshot(projectId, blob)
          // Dedupe: the backend also broadcasts this row via WS so the
          // capturer would otherwise see two list entries (one from the
          // POST response, one from the echo). Whoever arrives first wins.
          setScreenshots((prev) =>
            prev.some((s) => s.id === saved.id) ? prev : [saved, ...prev],
          )
        } catch (err) {
          console.error('[Screenshots] upload failed', err)
          const msg = err instanceof Error ? err.message : String(err)
          setScreenshotError(
            `Upload fehlgeschlagen: ${msg}. Wurde das Backend nach den Migrationen neu gestartet?`,
          )
        }
      } else {
        // `?dev=<url>` mode has no backend — fall back to an immediate
        // local download so the capture isn't wasted.
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
        a.download = `screenshot-${ts}.png`
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      }
    } finally {
      setScreenshotBusy(false)
    }
  }, [projectId, screenshotBusy])

  const handleDeleteScreenshot = useCallback(
    (id: string) => {
      setScreenshots((prev) => prev.filter((s) => s.id !== id))
      if (projectId) {
        deleteScreenshot(projectId, id).catch((err) => {
          console.warn('[Screenshots] delete failed', id, err)
        })
      }
    },
    [projectId],
  )

  const handleDownloadScreenshot = useCallback(
    async (item: ServerScreenshot) => {
      if (!projectId || !item.url) {
        // Dev mode (no project) — open the blob URL directly.
        if (item.url) window.open(item.url, '_blank', 'noreferrer')
        return
      }
      try {
        const { url, filename } = await getScreenshotDownloadUrl(projectId, item.id)
        // The presigned URL carries Content-Disposition: attachment, so the
        // browser downloads instead of opening — no target="_blank" needed.
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.rel = 'noreferrer'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } catch (err) {
        console.warn('[Screenshots] download failed, falling back to inline URL', err)
        window.open(item.url, '_blank', 'noreferrer')
      }
    },
    [projectId],
  )

  const handleDownloadAllScreenshots = useCallback(async () => {
    if (!projectId || screenshots.length === 0 || zipBusy) return
    setZipBusy(true)
    setScreenshotError(null)
    try {
      // Fetch each screenshot in parallel via signed URL → raw bytes →
      // packed into a single ZIP (STORE method, no extra compression).
      const entries: ZipEntry[] = await Promise.all(
        screenshots.map(async (s) => {
          const { url, filename } = await getScreenshotDownloadUrl(projectId, s.id)
          const resp = await fetch(url)
          if (!resp.ok) throw new Error(`HTTP ${resp.status} for ${filename}`)
          return { filename, bytes: new Uint8Array(await resp.arrayBuffer()) }
        }),
      )
      const blob = makeZipBlob(entries)
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `screenshots-${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500)
    } catch (err) {
      console.error('[Screenshots] zip download failed', err)
      const msg = err instanceof Error ? err.message : String(err)
      setScreenshotError(`ZIP-Download fehlgeschlagen: ${msg}`)
    } finally {
      setZipBusy(false)
    }
  }, [projectId, screenshots, zipBusy])

  // Propagate colour changes to both tools immediately (impacts new
  // segments / next polygon — already-placed measurements keep theirs).
  useEffect(() => {
    distanceToolRef.current?.setColor(color)
    areaToolRef.current?.setColor(color)
    noteToolRef.current?.setColor(color)
  }, [color])

  useEffect(() => {
    distanceToolRef.current?.setLineScale(lineScale)
    areaToolRef.current?.setLineScale(lineScale)
  }, [lineScale])

  useEffect(() => {
    // Textgröße slider scopes to notes only.
    noteToolRef.current?.setLabelScale(labelScale)
  }, [labelScale])

  useEffect(() => {
    // Maße slider scopes to measurement labels (m, m², Σ) only.
    distanceToolRef.current?.setLabelScale(measureScale)
    areaToolRef.current?.setLabelScale(measureScale)
  }, [measureScale])

  const handleToggleMeasurement = useCallback((id: string) => {
    setMeasurements((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m
        m.group.visible = !m.visible
        return { ...m, visible: !m.visible }
      }),
    )
  }, [])

  const handleRemoveMeasurement = useCallback((id: string) => {
    setMeasurements((prev) => {
      const target = prev.find((m) => m.id === id)
      if (!target) return prev
      if (target.kind === 'distance') {
        distanceToolRef.current?.removeMeasurement(id, target.group)
      } else if (target.kind === 'area') {
        areaToolRef.current?.removeMeasurement(id, target.group)
      } else {
        noteToolRef.current?.removeMeasurement(id, target.group)
      }
      // Persist the delete when in a real project (dev mode is ephemeral).
      // 404 is fine (race with another user).
      if (projectId) {
        deleteAnnotation(projectId, id).catch((err) => {
          console.warn('[Annotations] delete failed', id, err)
        })
      }
      return prev.filter((m) => m.id !== id)
    })
  }, [projectId])

  const handleClearAllMeasurements = useCallback(() => {
    setMeasurements((prev) => {
      for (const m of prev) {
        if (m.kind === 'distance') distanceToolRef.current?.removeMeasurement(m.id, m.group)
        else if (m.kind === 'area') areaToolRef.current?.removeMeasurement(m.id, m.group)
        else noteToolRef.current?.removeMeasurement(m.id, m.group)
        if (projectId) {
          deleteAnnotation(projectId, m.id).catch((err) => {
            console.warn('[Annotations] delete failed', m.id, err)
          })
        }
      }
      return []
    })
  }, [projectId])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Renderer — same setup as desktop scene.js
    const renderer = new WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true, // needed so the Drucken button can grab the framebuffer
    })
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(container.clientWidth, container.clientHeight)
    renderer.setClearColor(0x1a1a2e)
    renderer.outputColorSpace = SRGBColorSpace
    container.appendChild(renderer.domElement)

    // Camera — Z-up, FOV 60, identical initial pose to desktop scene.js
    const camera = new PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      0.01,
      10000,
    )
    camera.up.set(0, 0, 1)
    camera.position.set(0, -10, 8)
    camera.lookAt(0, 0, 0)

    const scene = new ThreeScene()

    // Screenshot capture — render once for an up-to-date framebuffer and
    // hand back a JPEG blob (quality 0.92 — visually lossless, ~3–10×
    // smaller than PNG so the upload + storage stay cheap). UI overlays
    // (toolbar, panels, tooltip) live in HTML so they're never in the
    // canvas; we just hide the in-scene cursor sprites of other teammates
    // so the saved frame isn't littered with their pointers.
    captureScreenshotRef.current = () =>
      new Promise<Blob | null>((resolve) => {
        const cursorHosts: { obj: import('three').Object3D; was: boolean }[] = []
        scene.traverse((obj) => {
          if (obj.name && obj.name.startsWith('cursor-')) {
            cursorHosts.push({ obj, was: obj.visible })
            obj.visible = false
          }
        })
        renderer.render(scene, camera)
        renderer.domElement.toBlob(
          (blob) => {
            for (const c of cursorHosts) c.obj.visible = c.was
            resolve(blob)
          },
          'image/jpeg',
          0.92,
        )
      })

    // Print current canvas — render once for a fresh framebuffer, grab a
    // PNG data URL, open a minimal popup that auto-fires window.print().
    // No headers/footers/UI — pure picture.
    printFnRef.current = () => {
      try {
        renderer.render(scene, camera)
        const url = renderer.domElement.toDataURL('image/png')
        // Use a hidden iframe instead of a popup window — no extra browser
        // chrome, no popup blocker friction, no "about:blank" header in the
        // preview.  We isolate via @media print so only the image hits paper.
        const iframe = document.createElement('iframe')
        iframe.setAttribute('aria-hidden', 'true')
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;'
        document.body.appendChild(iframe)
        const doc = iframe.contentDocument
        if (!doc) {
          document.body.removeChild(iframe)
          return
        }
        doc.open()
        doc.write(`<!doctype html><html><head><title>Druck</title>
<style>
  @page { margin: 0; }
  html,body { margin:0; padding:0; background:#fff; }
  img { display:block; width:100%; height:auto; }
</style></head><body>
<img src="${url}" />
</body></html>`)
        doc.close()
        const img = doc.querySelector('img')
        const fire = () => {
          try {
            iframe.contentWindow?.focus()
            iframe.contentWindow?.print()
          } finally {
            // Defer removal until after the print dialog closes.
            setTimeout(() => {
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
            }, 1000)
          }
        }
        if (img && !img.complete) img.addEventListener('load', fire, { once: true })
        else setTimeout(fire, 50)
      } catch (err) {
        console.error('[Print] failed', err)
      }
    }

    const controls = new Controls(camera, renderer.domElement)
    const coords = new CoordinateManager()

    let tilesRenderer: TilesRenderer | null = null
    let picking: Picking | null = null
    let disposed = false
    let wsAnnotationUnsub: (() => void) | null = null
    let wsScreenshotUnsub: (() => void) | null = null

    // Load tileset: detect mode then call loadTiles (1:1 with desktop
    // tiles-loader.js loadTiles signature including the `mode` arg).
    detectMode(tilesetUrl)
      .then((mode: TilesetMode) => {
        if (disposed) return mode
        console.log(`[Scene] tileset mode: ${mode}`)
        const loaded = loadTiles(
          scene,
          camera,
          renderer,
          tilesetUrl,
          controls,
          coords,
          () => {
            // onReady — fired the first time UTM offset is established
            setStatusMessage('Bereit')
            // Capture a thumbnail after the scene settles. 3 s is enough for
            // the first batch of tiles to land at the framed camera. Best-
            // effort — swallow errors so the viewer keeps working if upload
            // or canvas grab fails (e.g. bucket ACL, network, expired share).
            if (captureThumbnailFor) {
              window.setTimeout(() => {
                if (disposed) return
                try {
                  renderer.render(scene, camera)
                  renderer.domElement.toBlob(
                    (blob) => {
                      if (!blob || disposed) return
                      uploadThumbnail(captureThumbnailFor, blob).catch((err) => {
                        console.warn('[Thumbnail] upload failed:', err)
                      })
                    },
                    'image/jpeg',
                    0.85,
                  )
                } catch (err) {
                  console.warn('[Thumbnail] capture failed:', err)
                }
              }, 3000)
            }
          },
          mode,
        )
        tilesRenderer = loaded.tilesRenderer

        // Picking on the loaded group (so raycasts respect any ECEF
        // transform applied to it). The fallback set lets picking survive the
        // renderer culling a tile out of the group when you zoom in close.
        picking = new Picking(
          renderer.domElement,
          camera,
          loaded.group,
          () => Array.from(loaded.loadedScenes),
        )
        controls.setPickFn((x, y) => picking?.pickSurface(x, y) ?? null)

        distanceToolRef.current = new DistanceTool(scene, color)
        areaToolRef.current = new AreaTool(scene, color)
        noteToolRef.current = new NoteTool(scene, color)

        // Restore any annotations persisted by previous sessions (own +
        // teammates'). We do this BEFORE wiring the create-listener so
        // restored items don't get re-POSTed.
        if (initialAnnotations && initialAnnotations.length > 0) {
          const restored = restoreAnnotations(initialAnnotations, {
            distance: distanceToolRef.current,
            area: areaToolRef.current,
            note: noteToolRef.current,
          })
          if (restored.length) setMeasurements(restored)
        }

        const onAdd = (ev: { added?: Measurement; removed?: string }) => {
          if (!ev.added) return
          if (!projectId) {
            // Dev mode (`?dev=<url>`): no project to persist against.
            setMeasurements((prev) => [...prev, ev.added!])
            return
          }
          const m: Measurement = { ...ev.added, pending: true }
          setMeasurements((prev) => [...prev, m])
          createAnnotation(projectId, toCreatePayload(m))
            .then((saved) => {
              setMeasurements((prev) =>
                prev.map((x) => {
                  if (x.id !== m.id) return x
                  const updated: Measurement = {
                    ...x,
                    pending: false,
                    createdBy: saved.created_by,
                    createdAt: saved.created_at,
                  }
                  // Once we know the author, stamp the chip on the group
                  // so the toggle and hover tooltip have something to show.
                  ensureAuthorChip(updated, showAuthorsRef.current)
                  return updated
                }),
              )
            })
            .catch((err) => {
              console.warn('[Annotations] create failed', m.id, err)
              setMeasurements((prev) =>
                prev.map((x) => (x.id === m.id ? { ...x, pending: false } : x)),
              )
            })
        }
        distanceToolRef.current.setListener(onAdd)
        areaToolRef.current.setListener(onAdd)
        noteToolRef.current.setListener(onAdd)

        // Cursor manager — one sphere+sprite per remote user.
        cursorsManagerRef.current = new CursorsManager(scene)
        cursorsManagerRef.current.setMembers(realtimeRef.current.members)

        // Subscribe to realtime annotation echoes: another user's create
        // or delete must update our local scene + state. Own writes are
        // de-duped by id.
        // Live screenshot feed — when another user captures (or deletes)
        // a screenshot, mirror it into our gallery without a refresh.
        // Own POST responses also echo back here; dedupe by id.
        wsScreenshotUnsub = realtimeRef.current.onScreenshot((ev) => {
          if (ev.type === 'screenshot:create') {
            setScreenshots((prev) => {
              if (prev.some((s) => s.id === ev.screenshot.id)) return prev
              return [ev.screenshot, ...prev]
            })
          } else {
            setScreenshots((prev) => prev.filter((s) => s.id !== ev.id))
          }
        })

        wsAnnotationUnsub = realtimeRef.current.onAnnotation((ev) => {
          if (ev.type === 'annotation:create') {
            setMeasurements((prev) => {
              if (prev.some((m) => m.id === ev.annotation.id)) return prev
              const tools = {
                distance: distanceToolRef.current!,
                area: areaToolRef.current!,
                note: noteToolRef.current!,
              }
              try {
                const m = measurementFromServer(ev.annotation, tools)
                return [...prev, m]
              } catch (err) {
                console.warn('[realtime] could not restore', ev.annotation.id, err)
                return prev
              }
            })
            return
          }
          // annotation:delete
          setMeasurements((prev) => {
            const target = prev.find((m) => m.id === ev.id)
            if (!target) return prev
            if (target.kind === 'distance') distanceToolRef.current?.removeMeasurement(ev.id, target.group)
            else if (target.kind === 'area') areaToolRef.current?.removeMeasurement(ev.id, target.group)
            else noteToolRef.current?.removeMeasurement(ev.id, target.group)
            return prev.filter((m) => m.id !== ev.id)
          })
        })

        // Top-down raycast probe — finds the highest mesh point under
        // a given (x, y).  Used by AreaTool to lift the Fläche label
        // above kerbs / steps inside the polygon.
        const topProbe = (x: number, y: number): Vector3 | null => {
          const probe = new Raycaster()
          probe.set(new Vector3(x, y, 10_000), new Vector3(0, 0, -1))
          const hits = probe.intersectObject(loaded.group, true)
          for (const hit of hits) {
            if (!hit.object.visible) continue
            return hit.point.clone()
          }
          return null
        }
        areaToolRef.current.setSurfaceTopProbe(topProbe)
        return mode
      })
      .catch((err) => {
        console.error('[Scene] tileset load failed', err)
        setStatusMessage(`Fehler beim Laden: ${err?.message ?? err}`)
      })

    // Drag-vs-click discrimination: track mousedown position, only fire
    // the tool's handleClick if the cursor barely moved before mouseup.
    let downPos: { x: number; y: number; button: number } | null = null
    const onMouseDown = (event: MouseEvent) => {
      downPos = { x: event.clientX, y: event.clientY, button: event.button }
      // Hide the bottom-right navigation hint once the user starts
      // interacting — they clearly know how to use a mouse by now.
      setShowHint(false)
    }
    const onMouseUp = (event: MouseEvent) => {
      if (!downPos) return
      const dx = event.clientX - downPos.x
      const dy = event.clientY - downPos.y
      const movedPx = Math.hypot(dx, dy)
      const button = downPos.button
      downPos = null
      if (movedPx > CLICK_DRAG_THRESHOLD_PX) return // drag, not a click

      const tool = activeToolRef.current
      if (tool === 'none') return

      // Right-click finishes the current measurement
      if (button === 2) {
        if (tool === 'distance') distanceToolRef.current?.finish()
        else if (tool === 'area') areaToolRef.current?.close()
        return
      }

      // Left-click → pick + handle
      if (button !== 0) return
      if (!picking) return
      const hit = picking.pickSurface(event.clientX, event.clientY)
      if (!hit) return
      if (tool === 'distance') distanceToolRef.current?.handleClick(hit)
      else if (tool === 'area') areaToolRef.current?.handleClick(hit)
      else if (tool === 'note') noteToolRef.current?.handleClick(hit)
    }
    renderer.domElement.addEventListener('mousedown', onMouseDown)
    renderer.domElement.addEventListener('mouseup', onMouseUp)

    // Broadcast cursor position to other room members + hover-test against
    // measurements for the tooltip. Hover raycast is cheap (few groups,
    // depthTest:false geometry) so we run it on every mousemove.
    const hoverRay = new Raycaster()
    // Generous threshold for thin Line raycast so the user doesn't have to
    // hit the exact pixel-thin midline — the meter label sprite is the big
    // target but it's nice to also catch the line itself.
    hoverRay.params.Line = { threshold: 0.15 }
    const ndc = new Vector2()
    const onMouseMove = (event: MouseEvent) => {
      // Hover detection (always on, even in `?dev=` mode where there's no
      // backend — local measurements still get tooltips).
      const rect = renderer.domElement.getBoundingClientRect()
      ndc.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      )
      hoverRay.setFromCamera(ndc, camera)
      const targets = measurementsRef.current
        .filter((m) => m.visible)
        .map((m) => m.group)
      const hits = targets.length ? hoverRay.intersectObjects(targets, true) : []
      // Walk every hit (not just the closest) so a label sprite, a line, or
      // a marker cone all trigger the tooltip — whichever the cursor lands on.
      let hitId: string | null = null
      for (const hit of hits) {
        let obj: import('three').Object3D | null = hit.object
        while (obj && !obj.userData?.measurementId) obj = obj.parent
        if (obj?.userData?.measurementId) {
          hitId = obj.userData.measurementId as string
          break
        }
      }
      if (hitId) {
        setHover({ id: hitId, x: event.clientX, y: event.clientY })
      } else {
        setHover((prev) => (prev ? null : prev))
      }

      // Cursor broadcast (real project only).
      if (!picking || !projectId) return
      const hit = picking.pickSurface(event.clientX, event.clientY)
      if (hit) {
        realtimeRef.current.emitCursor({ x: hit.x, y: hit.y, z: hit.z })
      } else {
        realtimeRef.current.emitCursor(null)
      }
    }
    const onMouseLeave = () => {
      setHover(null)
      if (projectId) realtimeRef.current.emitCursor(null)
    }
    renderer.domElement.addEventListener('mousemove', onMouseMove)
    renderer.domElement.addEventListener('mouseleave', onMouseLeave)

    // Suppress browser context menu over the canvas (right-click handled above).
    const onContextMenu = (event: MouseEvent) => {
      event.preventDefault()
    }
    renderer.domElement.addEventListener('contextmenu', onContextMenu)

    // Animate
    const animate = () => {
      if (disposed) return
      controls.update()
      if (tilesRenderer) updateTiles(tilesRenderer, camera, renderer)
      // Sync remote cursors map → 3D scene every frame; the lerp inside
      // tick() turns the 10 Hz network feed into smooth motion, and the
      // camera reference lets it scale cursors to a constant screen size.
      if (cursorsManagerRef.current) {
        cursorsManagerRef.current.sync(realtimeRef.current.remoteCursors)
        cursorsManagerRef.current.tick(camera)
      }
      renderer.render(scene, camera)
      requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)

    // Resize
    const onResize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
      if (tilesRenderer) tilesRenderer.setResolutionFromRenderer(camera, renderer)
      distanceToolRef.current?.setResolution(w, h)
      areaToolRef.current?.setResolution(w, h)
    }
    window.addEventListener('resize', onResize)

    return () => {
      disposed = true
      printFnRef.current = null
      captureScreenshotRef.current = null
      window.removeEventListener('resize', onResize)
      renderer.domElement.removeEventListener('mousedown', onMouseDown)
      renderer.domElement.removeEventListener('mouseup', onMouseUp)
      renderer.domElement.removeEventListener('mousemove', onMouseMove)
      renderer.domElement.removeEventListener('mouseleave', onMouseLeave)
      renderer.domElement.removeEventListener('contextmenu', onContextMenu)
      wsAnnotationUnsub?.()
      wsScreenshotUnsub?.()
      cursorsManagerRef.current?.dispose()
      cursorsManagerRef.current = null
      distanceToolRef.current?.dispose()
      areaToolRef.current?.dispose()
      noteToolRef.current?.dispose()
      tilesRenderer?.dispose()
      controls.dispose()
      renderer.dispose()
      if (renderer.domElement.parentElement) {
        renderer.domElement.parentElement.removeChild(renderer.domElement)
      }
    }
  }, [tilesetUrl])

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <Toolbar
        active={activeTool}
        onChange={setActiveTool}
        color={color}
        onColorChange={setColor}
        onPrint={handlePrint}
        onScreenshot={handleScreenshot}
        screenshotBusy={screenshotBusy}
        lineScale={lineScale}
        onLineScaleChange={setLineScale}
        labelScale={labelScale}
        onLabelScaleChange={setLabelScale}
        measureScale={measureScale}
        onMeasureScaleChange={setMeasureScale}
        showAuthors={showAuthors}
        onShowAuthorsChange={setShowAuthors}
      />
      <div
        style={{
          // Right-column stack: Messungen on top, Bilder below it. The
          // column itself is absolute-positioned; the panels inside are
          // plain flex items so a long measurement list pushes Bilder
          // further down without ever overlapping.
          position: 'absolute',
          top: 64,
          right: 16,
          width: 260,
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        <MeasurementsPanel
          items={measurements}
          onToggle={handleToggleMeasurement}
          onRemove={handleRemoveMeasurement}
          onClearAll={handleClearAllMeasurements}
        />
        <ScreenshotsPanel
          items={screenshots}
          onDelete={handleDeleteScreenshot}
          onDownload={handleDownloadScreenshot}
          onDownloadAll={handleDownloadAllScreenshots}
          zipBusy={zipBusy}
          defaultOpen
          alwaysVisible={!!projectId}
          error={screenshotError}
          busy={screenshotBusy}
        />
      </div>
      {projectId && (
        <PresencePanel members={realtime.members} status={realtime.status} />
      )}
      {hover && (() => {
        const m = measurements.find((x) => x.id === hover.id)
        return m ? <MeasurementTooltip measurement={m} x={hover.x} y={hover.y} /> : null
      })()}
      <StatusBar message={statusMessage} showHint={showHint} />

      {/* Gontec-MV watermark — bottom-left, raised above the StatusBar strip */}
      <img
        src="/gontec-logo.jpg"
        alt="Gontec-MV"
        draggable={false}
        style={{
          position: 'absolute',
          bottom: 52,
          left: 12,
          width: 64,
          height: 'auto',
          borderRadius: 3,
          opacity: 0.85,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />

      {/* Top-right control group: back to project list + logout */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          gap: 8,
          zIndex: 10,
        }}
      >
        <a
          href="/"
          title="Zurück zur Projektliste"
          style={navButtonStyle}
          onMouseEnter={(e) => navHover(e, true)}
          onMouseLeave={(e) => navHover(e, false)}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>←</span>
          <span>Projekte</span>
        </a>
        <button
          onClick={() => {
            logout()
            // Force LoginGate to re-evaluate isAuthenticated. Reload keeps it
            // simple and also clears any in-memory tool state.
            window.location.reload()
          }}
          title="Abmelden"
          style={navButtonStyle}
          onMouseEnter={(e) => navHover(e, true)}
          onMouseLeave={(e) => navHover(e, false)}
        >
          Abmelden
        </button>
      </div>
    </div>
  )
}

const navButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: 'rgba(20,22,26,0.85)',
  color: '#e7e9ec',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  textDecoration: 'none',
  boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
  backdropFilter: 'blur(6px) saturate(140%)',
  WebkitBackdropFilter: 'blur(6px) saturate(140%)',
  transition: 'background 120ms ease, border-color 120ms ease',
  userSelect: 'none',
}

function navHover(e: React.SyntheticEvent<HTMLElement>, hovering: boolean): void {
  const el = e.currentTarget
  el.style.background = hovering ? 'rgba(40,44,52,0.95)' : 'rgba(20,22,26,0.85)'
  el.style.borderColor = hovering ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.18)'
}

